/** @jsx jsx */
import React, { useState, useContext, useCallback } from "react"
import { Link } from "gatsby"
import { useSpring, animated as a, config } from "react-spring"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import {
  Button,
  Flex,
  Grid,
  Box,
  Text,
  Link as ExternalLink,
  jsx,
} from "theme-ui"

import { Context } from "../../context/MainContext"

import Menu from "./Menu"

import useEventListener from "../../hooks/useEventListener"
import { underline } from "../../mixins"
import { isBrowser } from "../../util"
import enums from "../../util/enums"

import { DownArrow } from "../svg"

const styles = {
  container: {
    fontSize: ["32px", null, "42px", "50px"],
    color: "lightYellow",
    "a, button": {
      color: "lightYellow",
    },
    gridTemplateColumns: [
      "repeat(6, 1fr)",
      "repeat(6, 1fr)",
      "repeat(6, 1fr)",
      "repeat(12, 1fr)",
    ],
    gridGap: ["5px", "5px", "5px", 2],
  },
  first: {
    zIndex: 1,
    gridColumn: ["1 / 6", "1 / 5", "1 / 4", "1 / 6"],
  },
  second: {
    zIndex: 1,
    display: ["none", "none", "none", "flex"],
    gridColumn: "7 / 11",
    flexDirection: "column",
    alignItems: "flex-start",
    span: {
      whiteSpace: "nowrap",
    },
  },
  third: {
    zIndex: 1,
    gridColumn: ["6 / -1", "6 / -1", "6 / -1", "11 / -1"],
    flexDirection: "column",
    alignItems: "flex-end",
  },
}

const Navigation = ({ links = [], headline, scrollButtonText, cartCount }) => {
  const [isOpen, setOpen] = useState(false)
  const [hover, setHover] = useState(false)

  const { setIsCartOpen, setCaseBuilderMode } = useContext(Context)

  const spring = useSpring({
    config: config.wobbly,
    transform: hover ? "rotate(0deg)" : "rotate(-90deg)",
  })

  const handler = useCallback(() => {
    if (!isBrowser) return
    const offsetTop = document.getElementsByTagName("header")[0].clientHeight
    const scrollY = window.scrollY
    if (scrollY < offsetTop) {
      setOpen(false)
    }
  }, [])

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      setOpen(false)
    }
  }

  useEventListener("scroll", handler)

  return (
    <Grid sx={styles.container}>
      <Box sx={styles.first}>
        <Text as="p" sx={{ marginBottom: "1em" }} variant="body">
          {headline}
        </Text>
        <div
          sx={{
            display: "inline-block",
          }}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <AnchorLink
            variant="body"
            to="/#our-pints"
            sx={{
              paddingRight: "0.25em",
              ...underline("#F7E8A1"),
            }}
          >
            <span sx={{ marginRight: "0.25em" }}>{scrollButtonText}</span>
            <a.div style={spring} sx={{ display: "inline-block" }}>
              <DownArrow />
            </a.div>
          </AnchorLink>
        </div>
      </Box>

      <Flex as="div" sx={styles.second}>
        <Menu
          isOpen={isOpen}
          setOpen={setOpen}
          style={{ ...underline("#F7E8A1") }}
        >
          <AnchorLink tabIndex={isOpen ? 0 : -1} to="/#our-pints">
            <span
              onClick={() => setOpen(false)}
              onKeyDown={e => handleKeyDown(e)}
            >
              Our Pints
            </span>
          </AnchorLink>
          <AnchorLink tabIndex={isOpen ? 0 : -1} to="/#build-a-case">
            <span
              onClick={() => {
                setOpen(false)
                setCaseBuilderMode(enums.mode.bundle)
              }}
            >
              Build a Case
            </span>
          </AnchorLink>
          <AnchorLink tabIndex={isOpen ? 0 : -1} to="/#meet-marco">
            <span onClick={() => setOpen(false)}>Marco’s Story</span>
          </AnchorLink>
          <Link tabIndex={isOpen ? 0 : -1} to="/find-us" title="Find Us">
            <span>Find Us</span>
          </Link>
          <ExternalLink
            title="Shop Marco Merch"
            href="https://www.shop.marcoicecream.com/"
            target="_blank"
            rel="noopener"
          >
            <span>{"Merch"}</span>
          </ExternalLink>
          {links && links.length > 0
            ? links.map(link => (
                <div key={link.title}>
                  <Link
                    tabIndex={isOpen ? 0 : -1}
                    to={link.page.slug.current}
                    title={link.title}
                  >
                    <span>{link.title}</span>
                  </Link>
                </div>
              ))
            : null}
        </Menu>
      </Flex>

      <Flex sx={styles.third}>
        <Button
          onClick={() => setIsCartOpen(true)}
          sx={{
            padding: "0px",
            textAlign: "right",
            background: "none",
          }}
        >
          <Text as="p">Cart</Text>
          <Text as="p">{`(${cartCount})`}</Text>
        </Button>
      </Flex>
    </Grid>
  )
}

export default Navigation
